import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WellnessMetrics = ({ metrics, onUpdateMetric }) => {
  const [selectedMetric, setSelectedMetric] = useState(null);

  const getMetricIcon = (type) => {
    switch (type) {
      case 'steps':
        return 'Footprints';
      case 'water':
        return 'Droplets';
      case 'sleep':
        return 'Moon';
      case 'meditation':
        return 'Brain';
      case 'exercise':
        return 'Dumbbell';
      case 'nutrition':
        return 'Apple';
      case 'mood':
        return 'Smile';
      case 'energy':
        return 'Zap';
      default:
        return 'Activity';
    }
  };

  const getMetricColor = (type) => {
    switch (type) {
      case 'steps':
        return 'text-success';
      case 'water':
        return 'text-blue-500';
      case 'sleep':
        return 'text-purple-500';
      case 'meditation':
        return 'text-primary';
      case 'exercise':
        return 'text-success';
      case 'nutrition':
        return 'text-accent';
      case 'mood':
        return 'text-yellow-500';
      case 'energy':
        return 'text-orange-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const getProgressPercentage = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-success';
    if (percentage >= 75) return 'bg-accent';
    if (percentage >= 50) return 'bg-warning';
    return 'bg-error';
  };

  const formatValue = (value, unit) => {
    if (unit === 'hours' && value < 1) {
      return `${Math.round(value * 60)}m`;
    }
    return `${value}${unit}`;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-wellness">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-foreground">Today's Wellness Metrics</h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setSelectedMetric('add')}
        >
          Add Metric
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics?.map((metric) => {
          const progressPercentage = getProgressPercentage(metric?.current, metric?.target);
          const isCompleted = progressPercentage >= 100;
          
          return (
            <div
              key={metric?.id}
              className={`relative p-4 rounded-lg border transition-all duration-300 cursor-pointer hover:shadow-wellness-md ${
                isCompleted 
                  ? 'bg-success/5 border-success/20' :'bg-muted/30 border-border hover:border-primary/30'
              }`}
              onClick={() => setSelectedMetric(metric)}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={getMetricIcon(metric?.type)} 
                    size={20} 
                    className={getMetricColor(metric?.type)} 
                  />
                  <span className="text-sm font-medium text-foreground capitalize">{metric?.type}</span>
                </div>
                
                {isCompleted && (
                  <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center">
                    <Icon name="Check" size={14} color="white" />
                  </div>
                )}
              </div>
              <div className="mb-3">
                <div className="flex items-baseline space-x-1 mb-1">
                  <span className="text-2xl font-heading font-bold text-foreground">
                    {metric?.current}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    / {formatValue(metric?.target, metric?.unit)}
                  </span>
                </div>
                
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(progressPercentage)}`}
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{Math.round(progressPercentage)}% complete</span>
                <span>Updated {metric?.lastUpdated}</span>
              </div>
              {metric?.streak && metric?.streak > 0 && (
                <div className="absolute top-2 right-2">
                  <div className="flex items-center space-x-1 bg-accent/10 px-2 py-1 rounded-full">
                    <Icon name="Flame" size={12} className="text-accent" />
                    <span className="text-xs font-medium text-accent-foreground">{metric?.streak}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-foreground">Quick Actions</h4>
          <span className="text-xs text-muted-foreground">Tap to log instantly</span>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { action: 'water', label: 'Log Water', icon: 'Droplets' },
            { action: 'mood', label: 'Rate Mood', icon: 'Smile' },
            { action: 'exercise', label: 'Log Workout', icon: 'Dumbbell' },
            { action: 'meditation', label: 'Log Session', icon: 'Brain' }
          ]?.map((action) => (
            <Button
              key={action?.action}
              variant="ghost"
              size="sm"
              onClick={() => onUpdateMetric(action?.action)}
              className="flex flex-col items-center space-y-1 h-auto py-3"
            >
              <Icon name={action?.icon} size={20} className={getMetricColor(action?.action)} />
              <span className="text-xs">{action?.label}</span>
            </Button>
          ))}
        </div>
      </div>
      {/* Metric Detail Modal */}
      {selectedMetric && selectedMetric !== 'add' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border p-6 w-full max-w-md shadow-wellness-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-semibold text-foreground capitalize">
                Update {selectedMetric?.type}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedMetric(null)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div className="text-center">
                <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${
                  selectedMetric?.type === 'steps' ? 'from-success to-success/80' :
                  selectedMetric?.type === 'water' ? 'from-blue-500 to-blue-400' :
                  selectedMetric?.type === 'sleep'? 'from-purple-500 to-purple-400' : 'from-primary to-primary/80'
                } flex items-center justify-center mb-3`}>
                  <Icon 
                    name={getMetricIcon(selectedMetric?.type)} 
                    size={24} 
                    color="white" 
                  />
                </div>
                <p className="text-2xl font-heading font-bold text-foreground">
                  {formatValue(selectedMetric?.current, selectedMetric?.unit)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Target: {formatValue(selectedMetric?.target, selectedMetric?.unit)}
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUpdateMetric(selectedMetric?.type, 'decrease')}
                  iconName="Minus"
                />
                <div className="flex-1 text-center">
                  <span className="text-sm text-muted-foreground">Adjust value</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUpdateMetric(selectedMetric?.type, 'increase')}
                  iconName="Plus"
                />
              </div>
              
              <Button
                variant="default"
                fullWidth
                onClick={() => {
                  onUpdateMetric(selectedMetric?.type, 'save');
                  setSelectedMetric(null);
                }}
              >
                Save Update
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WellnessMetrics;